'use client';

import { Box, Button, Field, HStack, Input, Text, VStack } from '@chakra-ui/react';
import { AlertCircle, KeyRound, Megaphone, RefreshCw, ShieldCheck, Unlink } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useRouter } from 'next/navigation';
import { toast } from 'sonner';
import { useAuth } from '@/app/auth-provider';
import {
  IntegrationCard,
  IntegrationNotice,
  IntegrationStatusRow,
} from '@/components/shared/integrations/IntegrationCardShell';
import { InlineSpinner } from '@/components/shared/PageLoader';
import { useProject } from '@/contexts/ProjectContext';
import { canManageWorkspace } from '@/lib/workspace-access-core';

interface AdsSource {
  id: string;
  displayName: string;
  accountName: string | null;
  status: string;
  statusDetail: string | null;
  lastSyncAt: string | null;
  lastSyncStatus: string | null;
  lastSyncError: string | null;
}

interface AdsStatus {
  projectId: string;
  connected?: boolean;
  mutationsEnabled?: boolean;
  sources?: AdsSource[];
}

function statusTone(source?: AdsSource): 'connected' | 'warning' | 'idle' | 'danger' {
  if (!source) return 'idle';
  if (source.status === 'connected' && source.lastSyncStatus !== 'failed') return 'connected';
  if (source.status === 'error' || source.lastSyncStatus === 'failed') return 'danger';
  return 'warning';
}

function formatDate(value: string | null): string {
  if (!value) return 'Never';
  return new Date(value).toLocaleString();
}

export function OpenAiAdsIntegration({ onStatusChanged }: { onStatusChanged?: () => void }) {
  const statusRequestIdRef = useRef(0);
  const router = useRouter();
  const { user } = useAuth();
  const { currentProject } = useProject();
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState<AdsStatus | null>(null);
  const [apiKey, setApiKey] = useState('');
  const [saving, setSaving] = useState(false);
  const [syncingSource, setSyncingSource] = useState<string | null>(null);
  const [disconnectingSource, setDisconnectingSource] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const activeProjectIdRef = useRef(currentProject?.id);
  activeProjectIdRef.current = currentProject?.id;

  const projectStatus = status?.projectId === currentProject?.id ? status : null;
  const source = useMemo(() => projectStatus?.sources?.[0] || null, [projectStatus?.sources]);
  const canManageAds = canManageWorkspace(currentProject?.current_user_role);
  const mutationsEnabled = !loading && (projectStatus?.mutationsEnabled ?? false);
  const needsNewKey = source?.status === 'needs_access' || source?.status === 'error';

  const fetchStatus = useCallback(async () => {
    if (!user || !currentProject?.id) return;
    const projectId = currentProject.id;
    if (activeProjectIdRef.current !== projectId) return;
    const requestId = ++statusRequestIdRef.current;
    setLoading(true);
    setLoadError(null);
    try {
      const token = await user.getIdToken();
      const response = await fetch(
        `/api/integrations/ads/status?projectId=${encodeURIComponent(projectId)}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!response.ok) throw new Error('Failed to load OpenAI Ads status');
      const data = (await response.json()) as AdsStatus;
      if (requestId === statusRequestIdRef.current && data.projectId === projectId) {
        setStatus(data);
      }
    } catch (error) {
      console.error(error);
      if (requestId === statusRequestIdRef.current) {
        setLoadError('OpenAI Ads status could not be loaded.');
      }
    } finally {
      if (requestId === statusRequestIdRef.current) setLoading(false);
    }
  }, [currentProject?.id, user]);

  useEffect(() => {
    void fetchStatus();
  }, [fetchStatus]);

  useEffect(() => {
    setApiKey('');
  }, [currentProject?.id]);

  const connect = async () => {
    if (!user || !currentProject?.id || !canManageAds || !mutationsEnabled) return;
    setSaving(true);
    try {
      const token = await user.getIdToken();
      const response = await fetch('/api/integrations/ads/connect', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          provider: 'openai_ads',
          projectId: currentProject.id,
          apiKey,
          syncNow: false,
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || 'Connection failed');
      setApiKey('');
      toast.success('OpenAI Ads connected.');
      await fetchStatus();
      onStatusChanged?.();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Connection failed.');
    } finally {
      setSaving(false);
    }
  };

  const sync = async (target: AdsSource) => {
    if (!user || !canManageAds || !mutationsEnabled) return;
    setSyncingSource(target.id);
    try {
      const token = await user.getIdToken();
      const response = await fetch('/api/integrations/ads/sync', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ sourceId: target.id }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || data.status === 'failed') {
        throw new Error(data.errorMessage || data.error || 'Sync failed');
      }
      toast.success(`Synced ${data.rowsUpserted || 0} Ads rows.`);
      await fetchStatus();
      onStatusChanged?.();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Sync failed.');
      await fetchStatus();
      onStatusChanged?.();
    } finally {
      setSyncingSource(null);
    }
  };

  const disconnect = async (target: AdsSource) => {
    if (!user || !currentProject?.id || !canManageAds || !mutationsEnabled) return;
    setDisconnectingSource(target.id);
    try {
      const token = await user.getIdToken();
      const response = await fetch('/api/integrations/ads/disconnect', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ projectId: currentProject.id, sourceId: target.id }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || data.success === false) {
        throw new Error(data.error || 'Disconnect failed');
      }
      toast.success('OpenAI Ads disconnected.');
      await fetchStatus();
      onStatusChanged?.();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Disconnect failed.');
    } finally {
      setDisconnectingSource(null);
    }
  };

  if (!loadError && (loading || (status !== null && !projectStatus)) && !projectStatus) {
    return (
      <IntegrationCard
        icon={<Megaphone size={18} />}
        title="ChatGPT Ads"
        description="Paid visibility in ChatGPT moments through OpenAI Ads."
        statusLabel="Loading"
        statusTone="idle"
      >
        <IntegrationStatusRow icon={<InlineSpinner size="xs" />} title="Loading status" />
      </IntegrationCard>
    );
  }

  if (loadError && !projectStatus) {
    return (
      <IntegrationCard
        icon={<Megaphone size={18} />}
        title="ChatGPT Ads"
        description="Paid visibility in ChatGPT moments through OpenAI Ads."
        statusLabel="Unavailable"
        statusTone="danger"
      >
        <VStack align="stretch" gap="3" py="3">
          <IntegrationNotice tone="danger" icon={<AlertCircle size={16} />}>
            {loadError}
          </IntegrationNotice>
          <Box>
            <Button size="sm" variant="outline" onClick={() => void fetchStatus()}>
              <RefreshCw size={14} />
              <Text ml="1">Retry</Text>
            </Button>
          </Box>
        </VStack>
      </IntegrationCard>
    );
  }

  return (
    <IntegrationCard
      icon={<Megaphone size={18} />}
      title="ChatGPT Ads"
      description="Connect OpenAI Ads for paid visibility in ChatGPT moments."
      statusLabel={
        source
          ? statusTone(source) === 'connected'
            ? 'Connected'
            : 'Needs attention'
          : 'Needs setup'
      }
      statusTone={statusTone(source || undefined)}
      meta={<Text as="span">API key</Text>}
    >
      <VStack align="stretch" gap="0">
        {loadError ? (
          <IntegrationNotice tone="danger" icon={<AlertCircle size={16} />}>
            {loadError}
          </IntegrationNotice>
        ) : null}
        {!canManageAds ? (
          <IntegrationNotice tone="neutral" icon={<ShieldCheck size={16} />}>
            Workspace owners and admins manage this connection.
          </IntegrationNotice>
        ) : null}
        {canManageAds && !mutationsEnabled ? (
          <IntegrationNotice tone="neutral" icon={<ShieldCheck size={16} />}>
            OpenAI Ads actions are not enabled in this environment.
          </IntegrationNotice>
        ) : null}
        {source ? (
          <>
            <IntegrationStatusRow
              icon={<ShieldCheck size={16} />}
              title={source.accountName || source.displayName}
              description={`Last sync: ${formatDate(source.lastSyncAt)}`}
              action={
                canManageAds && mutationsEnabled ? (
                  <HStack gap="2">
                    {source.status !== 'needs_access' ? (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => sync(source)}
                        loading={syncingSource === source.id}
                      >
                        <RefreshCw size={14} />
                        <Text ml="1">Sync</Text>
                      </Button>
                    ) : null}
                    <Button
                      size="sm"
                      variant="outline"
                      colorPalette="red"
                      onClick={() => disconnect(source)}
                      loading={disconnectingSource === source.id}
                    >
                      <Unlink size={14} />
                      <Text ml="1">Disconnect</Text>
                    </Button>
                  </HStack>
                ) : undefined
              }
            />
            {source.lastSyncError ? (
              <IntegrationNotice tone="danger" icon={<AlertCircle size={16} />}>
                {source.lastSyncError}
              </IntegrationNotice>
            ) : null}
            {needsNewKey && canManageAds && mutationsEnabled ? (
              <VStack align="stretch" gap="3" py="3">
                <Field.Root>
                  <Field.Label>New OpenAI Ads API key</Field.Label>
                  <Input
                    type="password"
                    value={apiKey}
                    onChange={event => setApiKey(event.target.value)}
                    placeholder="sk-ads..."
                    autoComplete="off"
                  />
                </Field.Root>
                <Box>
                  <Button onClick={connect} loading={saving} disabled={!apiKey.trim()}>
                    Update API key
                  </Button>
                </Box>
              </VStack>
            ) : null}
            <Box py="3">
              <Button size="sm" variant="outline" onClick={() => router.push('/app/ads')}>
                Open Ads
              </Button>
            </Box>
          </>
        ) : (
          <VStack align="stretch" gap="3" py="3">
            <IntegrationNotice tone="neutral" icon={<KeyRound size={16} />}>
              Drafts can be planned in Ads before connection. Connect here before pushing to OpenAI
              Ads.
            </IntegrationNotice>
            <Field.Root>
              <Field.Label>OpenAI Ads API key</Field.Label>
              <Input
                disabled={!canManageAds || !mutationsEnabled}
                type="password"
                value={apiKey}
                onChange={event => setApiKey(event.target.value)}
                placeholder="sk-ads..."
                autoComplete="off"
              />
            </Field.Root>
            <Box>
              <Button
                onClick={connect}
                loading={saving}
                disabled={!canManageAds || !mutationsEnabled || !apiKey.trim()}
              >
                Connect OpenAI Ads
              </Button>
            </Box>
          </VStack>
        )}
      </VStack>
    </IntegrationCard>
  );
}
